import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Lock, Mail, Loader2, ArrowRight, ShieldCheck } from "lucide-react";

const inputCls =
  "w-full rounded-xl border border-ivory/10 bg-white/5 pl-12 pr-5 py-4 text-base text-ivory placeholder:text-ivory/30 transition-all duration-300 gold-focus";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-ivory flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="grain-overlay" aria-hidden="true" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 60% 50% at 50% 20%, rgba(212,175,55,0.08), transparent 60%)" }}
      />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="text-center mb-10">
          <a href="/" data-testid="admin-login-brand" className="font-serif text-3xl font-semibold tracking-tight">
            ASSU<span className="text-amber inline-block" style={{ transform: "translateY(-0.1em)" }}>–</span>FRANCE
          </a>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-ivory/40">
            Espace Courtier — Accès protégé
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="admin-login-form"
          className="rounded-3xl border hairline bg-cardbg p-8 sm:p-10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)] space-y-5"
        >
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ivory/30" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Adresse email"
              data-testid="admin-login-email-input"
              className={inputCls}
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ivory/30" />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe"
              data-testid="admin-login-password-input"
              className={inputCls}
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="group flex w-full items-center justify-center gap-3 rounded-full bg-amber px-8 py-4 text-base font-semibold text-obsidian transition-all duration-300 hover:bg-amber-warm hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Connexion…
              </>
            ) : (
              <>
                Accéder à mon espace
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </>
            )}
          </button>

          <p className="flex items-center justify-center gap-2 text-xs text-ivory/40">
            <ShieldCheck className="h-3.5 w-3.5 text-amber/70" />
            Connexion chiffrée — accès réservé à l'équipe ASSU-FRANCE
          </p>
        </form>
      </motion.div>
    </div>
  );
}
